import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Loader2,
  UserRound,
  Folder,
  Activity,
  Sparkles,
  Printer,
  FileSpreadsheet,
  FileDown,
  Clock,
  ArrowRight,
} from "lucide-react";
import HistoryPanel from "@/components/HistoryPanel";
import api from "@/api";
import { toast } from "sonner";
import XLSX from "xlsx-js-style";
import { fileDate } from "@/utils/excelStyle";
import { buildPrintDocument, openPrintWindow } from "@/utils/printLayout";

/**
 * Palette stato pratica per la mini timeline visuale (v1.9.0 · 22/07/2026).
 * I 4 stati canonici del gestionale sono mappati su colori distinguibili
 * a colpo d'occhio (< 3 secondi di lettura).
 */
const STATUS_COLORS = {
  "Nuova richiesta":  { bg: "#dbeafe", bar: "#2563eb", text: "#1e40af", label: "Nuova",    onBar: "#ffffff" },
  "In lavorazione":   { bg: "#fef3c7", bar: "#f59e0b", text: "#92400e", label: "In corso", onBar: "#7c2d12" },
  "Sospesa":          { bg: "#fee2e2", bar: "#dc2626", text: "#991b1b", label: "Sospesa",  onBar: "#ffffff" },
  "Conclusa":         { bg: "#dcfce7", bar: "#16a34a", text: "#15803d", label: "Conclusa", onBar: "#ffffff" },
};
const DEFAULT_STATUS = { bg: "#f1f5f9", bar: "#64748b", text: "#334155", label: "Altro", onBar: "#ffffff" };

function statusStyle(status) {
  return STATUS_COLORS[status] || DEFAULT_STATUS;
}

/**
 * Client360Dialog — "Cliente 360° Vero".
 *
 * v1.4.1 · 17/07/2026 (rev. sera).
 *   - Scroll interno funzionante (overflow-y-auto sul contenitore
 *     eventi con max-h calcolato) invece del ScrollArea shadcn che non
 *     ereditava l'altezza dal flex-parent.
 *   - Pulsanti Stampa e Esporta Excel per portare la timeline fuori
 *     dall'app (uso archivistico/legale).
 *   - Backend arricchisce ora operatorName (risolve gli ObjectId legacy).
 *
 * Aggrega in un'unica timeline cronologica gli eventi di TUTTE le
 * pratiche del cliente (fonte: GET /api/clients/{id}/timeline).
 */
export function Client360Dialog({ isOpen, onOpenChange, clientId, clientName }) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null); // {client, cases, events, totalCases, totalEvents}
  const [caseFilter, setCaseFilter] = useState("all");
  const [exporting, setExporting] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  useEffect(() => {
    if (!isOpen || !clientId) return;
    let cancelled = false;
    setLoading(true);
    setData(null);
    setCaseFilter("all");
    api
      .getClientTimeline(clientId)
      .then((res) => {
        if (!cancelled) setData(res);
      })
      .catch((err) => {
        if (!cancelled) {
          console.error("[Client360] load error", err);
          toast.error(err?.message || "Errore caricamento timeline cliente");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, clientId]);

  const filteredEvents = useMemo(() => {
    if (!data?.events) return [];
    if (caseFilter === "all") return data.events;
    return data.events.filter((e) => e.caseId === caseFilter);
  }, [data, caseFilter]);

  // Enrich events with a compact "Pratica GA-XXXX — Oggetto" prefix in
  // the details so l'utente vede da quale pratica proviene l'evento
  // anche quando è filtrato "Tutte".
  const eventsForPanel = useMemo(() => {
    if (!filteredEvents) return [];
    return filteredEvents.map((e) => {
      const showCasePrefix = caseFilter === "all" && e.caseNumber;
      const prefix = showCasePrefix
        ? `[${e.caseNumber}${e.caseSubject ? " · " + e.caseSubject : ""}] `
        : "";
      const details = e.details ? `${prefix}${e.details}` : prefix.trim();
      return { ...e, details };
    });
  }, [filteredEvents, caseFilter]);

  const casesList = data?.cases || [];
  const displayName = data?.client?.fullName || clientName || "Cliente";

  const formatTs = (iso) => {
    if (!iso) return "";
    try {
      return new Date(iso).toLocaleString("it-IT", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return iso;
    }
  };

  const handlePrint = () => {
    if (!data) return;
    const cs = data.cases || [];
    const evs = [...(data.events || [])].reverse(); // più recenti in cima
    const esc = (s) =>
      String(s ?? "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;");
    const rows = evs
      .map(
        (e) => `
      <tr>
        <td>${esc(formatTs(e.timestamp))}</td>
        <td>${esc(e.caseNumber || "")}</td>
        <td>${esc(e.action || "")}</td>
        <td>${esc(e.operatorName || e.operatorId || "")}</td>
        <td>${esc(e.details || "")}</td>
      </tr>`,
      )
      .join("");
    const casesRows = cs
      .map(
        (c) => `
      <tr>
        <td>${esc(c.caseNumber || "")}</td>
        <td>${esc(c.subject || "")}</td>
        <td>${esc(c.status || "")}</td>
        <td>${esc(c.practiceType || "")}</td>
        <td>${esc(formatTs(c.openingDate))}</td>
      </tr>`,
      )
      .join("");
    const bodyHtml = `
      <h2>Pratiche del cliente</h2>
      <table>
        <thead><tr><th>Numero</th><th>Oggetto</th><th>Stato</th><th>Tipologia</th><th>Data apertura</th></tr></thead>
        <tbody>${casesRows || '<tr><td colspan="5">Nessuna pratica</td></tr>'}</tbody>
      </table>
      <h2>Timeline eventi (più recenti in cima)</h2>
      <table>
        <thead><tr><th style="width:110px;">Data/Ora</th><th style="width:80px;">Pratica</th><th style="width:110px;">Azione</th><th style="width:140px;">Operatore</th><th>Dettagli</th></tr></thead>
        <tbody>${rows || '<tr><td colspan="5">Nessun evento</td></tr>'}</tbody>
      </table>`;
    const html = buildPrintDocument({
      title: `Cliente 360° — ${displayName}`,
      subtitle: `Pratiche: ${data.totalCases} · Eventi: ${data.totalEvents}`,
      bodyHtml,
      orientation: "landscape",
      studioName: "Studio Bonaiuti STP",
    });
    if (!openPrintWindow(html)) {
      toast.error("Popup bloccato dal browser — abilita i popup per stampare.");
    }
  };

  const handleExportExcel = () => {
    if (!data) return;
    try {
      setExporting(true);
      const evs = [...(data.events || [])].reverse();
      const rows = evs.map((e) => ({
        "Data/Ora": formatTs(e.timestamp),
        "Pratica": e.caseNumber || "",
        "Oggetto pratica": e.caseSubject || "",
        "Azione": e.action || "",
        "Operatore": e.operatorName || e.operatorId || "",
        "Dettagli": e.details || "",
      }));
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(rows, {
        header: ["Data/Ora", "Pratica", "Oggetto pratica", "Azione", "Operatore", "Dettagli"],
      });
      ws["!cols"] = [
        { wch: 18 }, { wch: 14 }, { wch: 32 }, { wch: 22 }, { wch: 22 }, { wch: 60 },
      ];
      XLSX.utils.book_append_sheet(wb, ws, "Timeline");

      // Second sheet: elenco pratiche
      const cRows = (data.cases || []).map((c) => ({
        "Numero": c.caseNumber || "",
        "Oggetto": c.subject || "",
        "Stato": c.status || "",
        "Tipologia": c.practiceType || "",
        "Data apertura": formatTs(c.openingDate),
      }));
      const ws2 = XLSX.utils.json_to_sheet(cRows, {
        header: ["Numero", "Oggetto", "Stato", "Tipologia", "Data apertura"],
      });
      ws2["!cols"] = [{ wch: 14 }, { wch: 36 }, { wch: 20 }, { wch: 22 }, { wch: 18 }];
      XLSX.utils.book_append_sheet(wb, ws2, "Pratiche");

      const safeName = displayName.replace(/[^a-zA-Z0-9À-ÿ_\-\s]/g, "").trim().replace(/\s+/g, "_");
      XLSX.writeFile(wb, `cliente_360_${safeName}_${fileDate()}.xlsx`);
      toast.success("Timeline esportata in Excel");
    } catch (err) {
      console.error("[Client360] export error", err);
      toast.error("Errore durante l'export Excel");
    } finally {
      setExporting(false);
    }
  };

  const handleDownloadPdf = async () => {
    if (!data) return;
    try {
      setDownloadingPdf(true);
      const { filename } = await api.downloadClientTimelinePDF(clientId, displayName);
      toast.success(`Dossier PDF scaricato: ${filename}`);
    } catch (err) {
      console.error("[Client360] PDF error", err);
      toast.error(err?.message || "Errore durante il download del PDF");
    } finally {
      setDownloadingPdf(false);
    }
  };

  // Deep-link alla pratica dalla timeline (17/07/2026 sera · v1.4.1).
  // Chiude la modale 360° e dispatcha un evento globale che App.js
  // ascolta per aprire il CaseDialog sulla pratica selezionata senza
  // dover fare navigazione manuale.
  const handleOpenCase = (caseId, caseNumber) => {
    onOpenChange(false);
    setTimeout(() => {
      window.dispatchEvent(
        new CustomEvent("gi:open-case-by-id", {
          detail: { caseId, caseNumber, source: "client-360" },
        }),
      );
    }, 200);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-4xl h-[90vh] flex flex-col p-0 gap-0"
        data-testid="client-360-dialog"
      >
        <DialogHeader className="px-6 pt-6 pb-3 border-b">
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-indigo-600" />
            Vista Cliente 360°
          </DialogTitle>
          <DialogDescription>
            Storico aggregato di tutte le pratiche di{" "}
            <span className="font-semibold text-slate-800">{displayName}</span>.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="space-y-3 p-8" data-testid="client-360-loading">
            <div className="flex items-center gap-2 text-sm text-slate-500">
              <Loader2 className="h-4 w-4 animate-spin" />
              Caricamento timeline in corso…
            </div>
            <Skeleton className="h-6 w-full" />
            <Skeleton className="h-6 w-2/3" />
            <Skeleton className="h-6 w-5/6" />
          </div>
        ) : !data ? (
          <div className="py-12 text-center text-sm text-slate-500">
            Nessun dato disponibile.
          </div>
        ) : (
          <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
            {/* Sticky header con KPI + chip: fisso mentre gli eventi scrollano */}
            <div className="px-6 py-3 border-b bg-slate-50/60 space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="rounded-lg border border-indigo-200 bg-white p-3 flex items-center gap-3">
                  <UserRound className="h-8 w-8 text-indigo-600" />
                  <div className="min-w-0">
                    <div className="text-[10px] text-indigo-800/70 uppercase tracking-wide font-semibold">
                      Cliente
                    </div>
                    <div className="text-sm font-semibold text-indigo-900 truncate" title={displayName}>
                      {displayName}
                    </div>
                  </div>
                </div>
                <div className="rounded-lg border border-emerald-200 bg-white p-3 flex items-center gap-3">
                  <Folder className="h-8 w-8 text-emerald-600" />
                  <div>
                    <div className="text-[10px] text-emerald-800/70 uppercase tracking-wide font-semibold">
                      Pratiche
                    </div>
                    <div className="text-2xl font-bold text-emerald-900" data-testid="client-360-cases-count">
                      {data.totalCases}
                    </div>
                  </div>
                </div>
                <div className="rounded-lg border border-amber-200 bg-white p-3 flex items-center gap-3">
                  <Activity className="h-8 w-8 text-amber-600" />
                  <div>
                    <div className="text-[10px] text-amber-800/70 uppercase tracking-wide font-semibold">
                      Eventi
                    </div>
                    <div className="text-2xl font-bold text-amber-900" data-testid="client-360-events-count">
                      {data.totalEvents}
                    </div>
                  </div>
                </div>
              </div>

              {casesList.length > 0 && (
                <div className="space-y-1" data-testid="client-360-visual-timeline">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1.5 text-[10px] gis-heading-wide uppercase tracking-wide text-slate-500">
                      <Clock className="h-3 w-3" />
                      Timeline pratiche · {casesList.length}
                    </div>
                    {caseFilter !== "all" && (
                      <button
                        type="button"
                        onClick={() => setCaseFilter("all")}
                        className="text-[10px] text-slate-500 hover:text-[var(--gis-green-dark)] gis-body underline underline-offset-2"
                        data-testid="client-360-clear-case-filter"
                      >
                        mostra tutte
                      </button>
                    )}
                  </div>
                  <div
                    className="flex flex-col gap-1.5"
                    data-testid="client-360-case-chips"
                  >
                    {/* All-cases pill (compatto) */}
                    <button
                      type="button"
                      onClick={() => setCaseFilter("all")}
                      className={`flex items-center gap-2 rounded-md border px-2 py-1 text-[11px] font-medium transition-colors text-left ${
                        caseFilter === "all"
                          ? "bg-slate-800 text-white border-transparent"
                          : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
                      }`}
                      data-testid="client-360-chip-all"
                    >
                      <span className="w-1 h-3 rounded-full bg-current opacity-60" />
                      Tutte
                      <span className={`ml-auto rounded-full px-1.5 min-w-[18px] text-center text-[10px] font-semibold ${caseFilter === "all" ? "bg-white/25" : "bg-slate-100"}`}>
                        {casesList.length}
                      </span>
                    </button>
                    {/* Mini timeline colorata: 1 barra per pratica */}
                    {casesList.map((c) => {
                      const active = caseFilter === c.id;
                      const st = statusStyle(c.status);
                      const openingDate = c.openingDate
                        ? new Date(c.openingDate).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "2-digit" })
                        : "—";
                      return (
                        <button
                          key={c.id}
                          type="button"
                          onClick={() => setCaseFilter(c.id)}
                          className={`group flex items-stretch gap-0 rounded-md border transition-all overflow-hidden ${
                            active
                              ? "border-[var(--gis-green)] shadow-sm ring-1 ring-[var(--gis-green-200)]"
                              : "border-slate-200 hover:border-slate-300"
                          }`}
                          data-testid={`client-360-chip-case-${c.caseNumber || c.id}`}
                          title={`${c.subject || c.caseNumber} · ${c.status || "—"} · aperta ${openingDate}`}
                        >
                          {/* Bar colorata verticale (color = status) */}
                          <span
                            className="w-1.5 shrink-0"
                            style={{ backgroundColor: st.bar }}
                            aria-hidden="true"
                          />
                          {/* Riga informazione */}
                          <span
                            className="flex-1 flex items-center gap-2 px-2 py-1 min-w-0"
                            style={{ backgroundColor: active ? "var(--gis-green-50)" : st.bg + "40" }}
                          >
                            <span className="font-mono text-[10px] font-semibold text-slate-500 shrink-0">
                              {c.caseNumber || "—"}
                            </span>
                            <span
                              className="text-[11px] gis-body font-medium truncate flex-1 min-w-0"
                              style={{ color: active ? "var(--gis-green-dark)" : "#334155" }}
                            >
                              {c.subject || <em className="text-slate-400">senza oggetto</em>}
                            </span>
                            {/* Chip stato */}
                            <span
                              className="text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded shrink-0"
                              style={{ backgroundColor: st.bar, color: st.onBar }}
                            >
                              {st.label}
                            </span>
                            <span className="text-[10px] text-slate-500 gis-body shrink-0 hidden sm:inline">
                              {openingDate}
                            </span>
                            <ArrowRight
                              className="h-3 w-3 shrink-0 transition-transform group-hover:translate-x-0.5"
                              style={{ color: active ? "var(--gis-green)" : "#94a3b8" }}
                            />
                          </span>
                        </button>
                      );
                    })}
                  </div>
                  {/* Legenda mini (2 righe) */}
                  <div className="flex items-center gap-3 pt-1 text-[10px] gis-body text-slate-500 flex-wrap">
                    <span>Stato:</span>
                    {Object.entries(STATUS_COLORS).map(([status, st]) => (
                      <span key={status} className="flex items-center gap-1">
                        <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: st.bar }} />
                        {st.label}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Corpo scrollabile: overflow-y-auto sul contenitore, HistoryPanel al suo interno */}
            <div
              className="flex-1 min-h-0 overflow-y-auto px-6 py-4"
              data-testid="client-360-scroll-container"
            >
              <HistoryPanel
                events={eventsForPanel}
                testId="client-360-history-panel"
                showSourceFilter
                onCaseClick={handleOpenCase}
              />
            </div>
          </div>
        )}

        <DialogFooter className="px-6 py-3 border-t bg-slate-50/40 gap-2 flex-row sm:justify-between">
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              onClick={handleDownloadPdf}
              disabled={!data || loading || downloadingPdf}
              className="border-rose-300 text-rose-700 hover:bg-rose-50"
              data-testid="client-360-pdf"
              title="Scarica dossier PDF (archivio/consegna cliente)"
            >
              {downloadingPdf ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <FileDown className="mr-2 h-4 w-4" />
              )}
              Dossier PDF
            </Button>
            <Button
              variant="outline"
              onClick={handlePrint}
              disabled={!data || loading}
              className="border-slate-300"
              data-testid="client-360-print"
              title="Stampa la timeline aggregata"
            >
              <Printer className="mr-2 h-4 w-4" /> Stampa
            </Button>
            <Button
              variant="outline"
              onClick={handleExportExcel}
              disabled={!data || loading || exporting}
              className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
              data-testid="client-360-export"
              title="Esporta la timeline in Excel"
            >
              {exporting ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <FileSpreadsheet className="mr-2 h-4 w-4" />
              )}
              Esporta Excel
            </Button>
          </div>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            data-testid="client-360-close"
          >
            Chiudi
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default Client360Dialog;

